// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

/**
 * The Live view's measured curves: grid intake and IT load.
 *
 * Both are single logical nodes resolved by aggregation profile (ADR-0012).
 *
 * Combined HVAC is deliberately absent. It is not one node but a set of ~20
 * members with no grouped read upstream, so drawing it meant twenty requests a
 * minute summed on the client. That is not a curve anyone can trust and it
 * dominated the page's request budget, so the HVAC card now shows the plan's
 * intended curve only until a grouped read exists. Cooling load, setpoint and
 * staging telemetry are still not published at all.
 */

import { useMemo } from 'react';
import { useLiveLogicalNodeSeries } from './useLiveLogicalNodeSeries';
import { resolveGridNode, resolveItLoadNode } from '../utils/logicalNodeResolution';
import type { LogicalNode } from '../types/monetization';
import type { RawInputs, TimeAxis } from '../types';

/** Everything a card needs to caption its own coverage. */
export interface LiveSeriesCoverage {
  /** Axis-aligned measured values; empty when nothing is available. */
  values: (number | null)[];
  /** Nodes asked for. */
  requested: number;
  /** Nodes that returned at least one measured value. */
  contributing: number;
  /** Ids the membership API does not publish for this site. */
  unknown: string[];
  /** Ids whose membership upstream has never been resolved (409). */
  unresolved: string[];
  /** Ids that 404'd or failed. */
  failed: string[];
  /**
   * Ids still drawing a cached series because their latest poll failed. They
   * count as contributing -- the curve is on screen -- so this is the only way a
   * card can tell the reading is not advancing.
   */
  stale: string[];
  /**
   * The most recent poll failed outright and the curve on screen is the cached
   * one. Distinct from `stale`, which names *members* still drawing a cached
   * reading behind a poll that otherwise succeeded.
   */
  pollFailed?: boolean;
  /**
   * The request itself failed and nothing is cached (a 5xx on the telemetry or
   * membership service). Without it a failure reads as "no node is published",
   * which blames the data for what is an outage.
   */
  requestFailed?: boolean;
  loading: boolean;
}

export interface UseLivePlanTelemetryResult {
  grid: LiveSeriesCoverage;
  itLoad: LiveSeriesCoverage;
}

const NO_IDS: string[] = [];

export function useLivePlanTelemetry(
  siteId: string | undefined,
  rawInputs: RawInputs | undefined,
  nodes: LogicalNode[],
  /** The axis to resample onto when there is no plan to supply one. */
  fallbackAxis?: TimeAxis
): UseLivePlanTelemetryResult {
  const timeAxis = rawInputs?.time_axis ?? fallbackAxis;

  const gridNode = useMemo(() => resolveGridNode(nodes, rawInputs), [nodes, rawInputs]);
  const itLoadNode = useMemo(() => resolveItLoadNode(nodes), [nodes]);

  const gridIds = useMemo(() => (gridNode ? [gridNode.logicalNodeId] : NO_IDS), [gridNode]);
  const itLoadIds = useMemo(() => (itLoadNode ? [itLoadNode.logicalNodeId] : NO_IDS), [itLoadNode]);

  const grid = useLiveLogicalNodeSeries(siteId, gridIds, timeAxis);
  const itLoad = useLiveLogicalNodeSeries(siteId, itLoadIds, timeAxis);

  const coverage = (
    series: ReturnType<typeof useLiveLogicalNodeSeries>,
    ids: string[]
  ): LiveSeriesCoverage => ({
    values: series.total,
    requested: ids.length,
    contributing: series.contributing,
    // Single-node series: there is no plan-named member for membership to be
    // missing, so nothing can land in `unknown`.
    unknown: NO_IDS,
    unresolved: series.unresolved,
    failed: series.failed,
    stale: series.stale,
    // Every node's read failed with nothing cached: an outage, not an empty
    // window. A 404 or 409 stays on the per-node "could not be read" path.
    requestFailed: series.nodes.length > 0 && series.nodes.every(n => n.failure === 'unavailable'),
    loading: series.loading,
  });

  return {
    grid: coverage(grid, gridIds),
    itLoad: coverage(itLoad, itLoadIds),
  };
}

/**
 * One line explaining why a live curve is partial or absent, or null when every
 * node reported. Kept next to the hook so the wording is derived from the same
 * facts the fetch produced rather than re-inferred in each card.
 */
export function describeCoverage(coverage: LiveSeriesCoverage): string | null {
  const { requested, contributing, unknown, unresolved, failed, stale, pollFailed } = coverage;

  // Nothing has been asked yet, so there is nothing to report. Without this the
  // card spends its first paint claiming every node reported no data.
  if (coverage.loading && contributing === 0) return null;

  if (coverage.requestFailed) {
    return 'Live readings could not be loaded — the request failed. The plan’s own curve is unaffected.';
  }

  if (requested === 0) {
    return 'No logical node is published for this series yet, so there is no live reading.';
  }
  if (contributing === requested && unknown.length === 0) {
    // Everything is drawing, but a poll failed and one of these curves is a
    // cached one. Say so plainly rather than reporting full coverage: the line
    // is real, it just stopped advancing.
    if (stale.length > 0 || pollFailed) {
      return 'Showing the last good reading — the most recent poll could not be read.';
    }
    return null;
  }

  const reasons: string[] = [];
  if (unknown.length > 0) {
    reasons.push(
      `${unknown.length} named by the plan ${
        unknown.length === 1 ? 'is' : 'are'
      } not published by the membership API`
    );
  }
  if (unresolved.length > 0) {
    reasons.push(
      `${unresolved.length} ${unresolved.length === 1 ? 'has' : 'have'} unresolved membership`
    );
  }
  if (failed.length > 0) {
    reasons.push(`${failed.length} could not be read`);
  }
  if (stale.length > 0) {
    reasons.push(
      `${stale.length} ${stale.length === 1 ? 'is' : 'are'} showing the last good reading`
    );
  } else if (pollFailed) {
    reasons.push('the most recent poll could not be read');
  }
  const silent = requested - contributing - unknown.length - unresolved.length - failed.length;
  if (silent > 0) reasons.push(`${silent} reported no data in this window`);

  const head =
    contributing === 0
      ? 'No live reading yet'
      : // "grouped assets" only means anything for a series summed over several
        // nodes. Grid and IT load are each one node, so it would be a lie.
        requested > 1
        ? `Live covers ${contributing} of ${requested} grouped assets`
        : `Live covers ${contributing} of ${requested} nodes`;
  return reasons.length > 0 ? `${head} — ${reasons.join(', ')}.` : `${head}.`;
}
